// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { DollarSign, Clock, FileText, Edit } from 'lucide-react';

interface PackageLevel {
  id: string;
  name: string;
  base_price: number;
  max_proofs: number;
  max_revisions: number;
  turnaround_days: number;
  description: string;
  features: string[];
}

interface PackageType {
  id: string;
  name: string;
  description: string;
  icon: string;
}

interface VendorOffering {
  id: string;
  package_level_id: string;
  package_type_id: string;
  custom_price?: number;
  custom_turnaround_days?: number;
  is_active: boolean;
}

const VendorPackageOfferingsManager: React.FC = () => {
  const { user } = useAuth();
  const [levels, setLevels] = useState<PackageLevel[]>([]);
  const [types, setTypes] = useState<PackageType[]>([]);
  const [offerings, setOfferings] = useState<VendorOffering[]>([]);
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingOffering, setEditingOffering] = useState<{ levelId: string; typeId: string; price?: number; turnaround?: number } | null>(null);

  useEffect(() => {
    if (user) {
      fetchVendorProfile();
      fetchLevelsAndTypes();
    }
  }, [user]);

  useEffect(() => {
    if (vendorId) {
      fetchOfferings();
    }
  }, [vendorId]);

  const fetchVendorProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('graphics_vendor_profiles')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (error) throw error;
      setVendorId(data.id);
    } catch (error: any) {
      console.error('Error fetching vendor profile:', error);
    }
  };

  const fetchLevelsAndTypes = async () => {
    try {
      const [levelsResponse, typesResponse] = await Promise.all([
        supabase
          .from('graphics_package_levels')
          .select('*')
          .eq('is_active', true)
          .order('display_order'),
        supabase
          .from('graphics_package_types')
          .select('*')
          .eq('is_active', true)
          .order('display_order')
      ]);

      if (levelsResponse.error) throw levelsResponse.error;
      if (typesResponse.error) throw typesResponse.error;

      setLevels(levelsResponse.data || []);
      setTypes(typesResponse.data || []);
    } catch (error: any) {
      console.error('Error fetching levels and types:', error);
      toast.error('Failed to load package levels and types');
    }
  };

  const fetchOfferings = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('graphics_vendor_package_offerings')
        .select('*')
        .eq('vendor_id', vendorId);

      if (error) throw error;
      setOfferings(data || []);
    } catch (error: any) {
      console.error('Error fetching offerings:', error);
      toast.error('Failed to load offerings');
    } finally {
      setIsLoading(false);
    }
  };

  const getOffering = (levelId: string, typeId: string): VendorOffering | undefined => {
    return offerings.find(o => o.package_level_id === levelId && o.package_type_id === typeId);
  };

  const toggleOffering = async (levelId: string, typeId: string, isActive: boolean) => {
    if (!vendorId) return;

    try {
      const existing = getOffering(levelId, typeId);

      if (existing) {
        const { error } = await supabase
          .from('graphics_vendor_package_offerings')
          .update({ is_active: isActive })
          .eq('id', existing.id);

        if (error) throw error;
      } else if (isActive) {
        const { error } = await supabase
          .from('graphics_vendor_package_offerings')
          .insert({
            vendor_id: vendorId,
            package_level_id: levelId,
            package_type_id: typeId,
            is_active: true
          });

        if (error) throw error;
      }

      toast.success('Offering updated successfully');
      fetchOfferings();
    } catch (error: any) {
      console.error('Error toggling offering:', error);
      toast.error('Failed to update offering');
    }
  };

  const updateCustomTerms = async (levelId: string, typeId: string, customPrice?: number, customTurnaround?: number) => {
    if (!vendorId) return;

    try {
      const existing = getOffering(levelId, typeId);
      if (!existing) return;

      const { error } = await supabase
        .from('graphics_vendor_package_offerings')
        .update({ 
          custom_price: customPrice,
          custom_turnaround_days: customTurnaround
        })
        .eq('id', existing.id);

      if (error) throw error;

      toast.success('Custom terms updated successfully');
      setEditingOffering(null);
      fetchOfferings();
    } catch (error: any) {
      console.error('Error updating custom terms:', error);
      toast.error('Failed to update custom terms');
    }
  };

  const startEditingTerms = (levelId: string, typeId: string) => {
    const existing = getOffering(levelId, typeId);
    const level = levels.find(l => l.id === levelId);
    
    setEditingOffering({
      levelId,
      typeId,
      price: existing?.custom_price || level?.base_price || 0,
      turnaround: existing?.custom_turnaround_days || level?.turnaround_days || 7
    });
  };

  if (isLoading) {
    return <div className="flex justify-center p-6">Loading your package offerings...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Package Offerings</h3>
        <p className="text-sm text-muted-foreground">
          Select which package levels you want to offer for each graphics type. You can customize pricing and turnaround times.
        </p>
      </div>

      <div className="grid gap-4">
        {types.map((type) => (
          <Card key={type.id}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <span className="text-xl">{type.icon}</span>
                {type.name}
              </CardTitle>
              {type.description && (
                <p className="text-sm text-muted-foreground">{type.description}</p>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {levels.map((level) => {
                const offering = getOffering(level.id, type.id);
                const isActive = offering?.is_active || false;
                const isEditing = editingOffering?.levelId === level.id && editingOffering?.typeId === type.id;

                return (
                  <div key={level.id} className={`p-4 border rounded-lg ${isActive ? 'border-primary bg-primary/5' : 'border-border'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={isActive}
                          onCheckedChange={(checked) => toggleOffering(level.id, type.id, !!checked)}
                        />
                        <div>
                          <h4 className="font-medium">{level.name}</h4>
                          <p className="text-sm text-muted-foreground">{level.description}</p>
                        </div>
                      </div>
                      
                      {isActive && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEditingTerms(level.id, type.id)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Customize
                        </Button>
                      )}
                    </div>

                    {isActive && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <span>
                            ${offering?.custom_price || level.base_price}
                            {offering?.custom_price && (
                              <Badge variant="outline" className="ml-1 text-xs">Custom</Badge>
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {offering?.custom_turnaround_days || level.turnaround_days} days
                            {offering?.custom_turnaround_days && (
                              <Badge variant="outline" className="ml-1 text-xs">Custom</Badge>
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span>{level.max_proofs} proofs</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Edit className="h-4 w-4 text-muted-foreground" />
                          <span>{level.max_revisions} revisions</span>
                        </div>
                      </div>
                    )}

                    {isEditing && (
                      <div className="mt-3 p-3 bg-muted rounded border">
                        <h5 className="text-sm font-medium mb-2">Customize Terms</h5>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-medium">Custom Price ($)</label>
                            <Input
                              type="number"
                              value={editingOffering.price}
                              onChange={(e) => setEditingOffering({
                                ...editingOffering,
                                price: parseFloat(e.target.value) || 0
                              })}
                              placeholder={`Default: $${level.base_price}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium">Custom Turnaround (days)</label>
                            <Input
                              type="number"
                              value={editingOffering.turnaround}
                              onChange={(e) => setEditingOffering({
                                ...editingOffering,
                                turnaround: parseInt(e.target.value) || 0
                              })}
                              placeholder={`Default: ${level.turnaround_days} days`}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button 
                            size="sm" 
                            onClick={() => updateCustomTerms(
                              level.id, 
                              type.id, 
                              editingOffering.price, 
                              editingOffering.turnaround
                            )}
                          >
                            Save
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => setEditingOffering(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default VendorPackageOfferingsManager;